import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { servicesApi, ServicesListParams, CreateServiceData, UpdateServiceData } from '../services/services';

// Query keys
export const serviceKeys = {
  all: ['services'] as const,
  lists: () => [...serviceKeys.all, 'list'] as const,
  list: (params: ServicesListParams) => [...serviceKeys.lists(), params] as const,
  details: () => [...serviceKeys.all, 'detail'] as const,
  detail: (id: string) => [...serviceKeys.details(), id] as const,
  stats: () => [...serviceKeys.all, 'stats'] as const,
  categories: () => [...serviceKeys.all, 'categories'] as const,
};

// List services
export function useServices(params: ServicesListParams = {}) {
  return useQuery({
    queryKey: serviceKeys.list(params),
    queryFn: () => servicesApi.list(params),
  });
}

// Get single service
export function useService(id: string) {
  return useQuery({
    queryKey: serviceKeys.detail(id),
    queryFn: () => servicesApi.get(id),
    enabled: !!id,
  });
}

// Get service stats
export function useServiceStats() {
  return useQuery({
    queryKey: serviceKeys.stats(),
    queryFn: () => servicesApi.getStats(),
  });
}

// Get categories
export function useServiceCategories() {
  return useQuery({
    queryKey: serviceKeys.categories(),
    queryFn: () => servicesApi.getCategories(),
  });
}

// Create service
export function useCreateService() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateServiceData) => servicesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: serviceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: serviceKeys.stats() });
    },
  });
}

// Update service
export function useUpdateService() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateServiceData }) =>
      servicesApi.update(id, data),
    onSuccess: (service) => {
      queryClient.invalidateQueries({ queryKey: serviceKeys.lists() });
      queryClient.setQueryData(serviceKeys.detail(service._id), service);
    },
  });
}

// Delete service
export function useDeleteService() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, permanent = false }: { id: string; permanent?: boolean }) =>
      servicesApi.delete(id, permanent),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: serviceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: serviceKeys.stats() });
    },
  });
}

// Toggle service status
export function useToggleServiceStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => servicesApi.toggleStatus(id),
    onSuccess: (service) => {
      queryClient.invalidateQueries({ queryKey: serviceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: serviceKeys.stats() });
      queryClient.setQueryData(serviceKeys.detail(service._id), service);
    },
  });
}

// Toggle service booking visibility
export function useToggleServiceBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => servicesApi.toggleBooking(id),
    onSuccess: (service) => {
      queryClient.invalidateQueries({ queryKey: serviceKeys.lists() });
      queryClient.setQueryData(serviceKeys.detail(service._id), service);
    },
  });
}

// Reorder services
export function useReorderServices() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (serviceIds: string[]) => servicesApi.reorder(serviceIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: serviceKeys.lists() });
    },
  });
}

// Duplicate service
export function useDuplicateService() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => servicesApi.duplicate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: serviceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: serviceKeys.stats() });
    },
  });
}
